/**
 * Carrousel a defilement natif.
 *
 * Le defilement, l'accroche et l'inertie sont entierement CSS
 * (`overflow-x: auto` + `scroll-snap-type`) : ce composant n'anime rien et ne
 * remplace aucun geste. Il ne fait que deux choses que le CSS ne sait pas
 * faire, et uniquement quand le contenu deborde reellement — donc jamais sur
 * ordinateur, ou la grille tient dans la largeur :
 *
 *  1. rendre la zone atteignable au clavier. Un conteneur defilant n'est pas
 *     focusable par defaut : sans `tabindex`, le contenu deborde sans qu'aucune
 *     touche ne permette d'y acceder. On ne le pose que lorsqu'il y a
 *     effectivement de quoi defiler, pour ne pas creer un arret de tabulation
 *     mort sur les grandes fenetres ;
 *  2. tenir a jour l'indicateur de position sous le carrousel ;
 *  3. poser la classe `is-scrollable` sur l'hote, seul moyen pour le CSS de
 *     masquer cet indicateur quand le contenu tient dans la largeur.
 *
 * Deux indicateurs sont possibles, chacun optionnel et choisi par l'appelant :
 *
 *  - `[data-carousel-dot]` : une pastille par element, la position etant
 *     l'element dont le bord gauche est cale. Lisible tant qu'un element occupe
 *     a peu pres toute la largeur visible — le cas de `multicolumn` ;
 *  - `[data-carousel-progress]` : une barre proportionnelle, pour les rangees
 *     ou plusieurs elements sont visibles a la fois. Des pastilles y mentiraient
 *     (avec 9 coloris dont 4 visibles, les trois dernieres ne peuvent jamais
 *     devenir actives), la barre non : elle rapporte la portion visible et sa
 *     position, sans etat mort.
 *
 * Les deux sont `aria-hidden` : la position est deja annoncee par la semantique
 * de liste (« element 2 sur 3 »), la redire serait du bruit.
 */
class GoCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-carousel-viewport]');
    if (!this.viewport) return;

    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.items = Array.from(this.viewport.querySelectorAll('[data-carousel-item]'));
    this.progress = this.querySelector('[data-carousel-progress]');

    this.onScroll = this.onScroll.bind(this);
    this.sync = this.sync.bind(this);

    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    // Le debordement depend de la largeur de la fenetre comme du chargement des
    // images : un ResizeObserver suit les deux, la ou un `resize` en raterait.
    this.observer = new ResizeObserver(this.sync);
    this.observer.observe(this.viewport);

    this.sync();
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.idle);
  }

  get scrollable() {
    return this.viewport.scrollWidth - this.viewport.clientWidth > 1;
  }

  sync() {
    if (this.scrollable) {
      this.viewport.setAttribute('tabindex', '0');
      this.viewport.setAttribute('role', 'region');
    } else {
      this.viewport.removeAttribute('tabindex');
      this.viewport.removeAttribute('role');
    }

    // Le CSS ne sait pas si le contenu deborde : sans cette classe, l'indicateur
    // resterait affiche sous une rangee qui tient entierement dans la largeur.
    this.classList.toggle('is-scrollable', this.scrollable);

    this.update();
  }

  onScroll() {
    // L'indicateur se comporte comme une barre de defilement en surimpression :
    // present pendant le geste, efface apres une pause. La classe ne fait
    // qu'annoncer l'etat — c'est le CSS qui decide quoi en montrer, et les
    // pastilles de `multicolumn`, qui ne s'en servent pas, restent inchangees.
    this.classList.add('is-scrolling');
    clearTimeout(this.idle);
    this.idle = setTimeout(() => this.classList.remove('is-scrolling'), 800);

    // Le defilement tactile emet des dizaines d'evenements par seconde : on
    // n'en retient qu'un par image, l'indicateur n'ayant pas besoin de plus.
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  }

  update() {
    if (this.progress) {
      // Deux nombres sans unite, que le CSS convertit en largeur et en decalage
      // du curseur. `scrollWidth` vaut 0 tant que le conteneur est masque : on
      // retombe alors sur une barre pleine, l'etat au repos.
      const overflow = this.viewport.scrollWidth - this.viewport.clientWidth;
      const visible = this.viewport.scrollWidth > 0 ? this.viewport.clientWidth / this.viewport.scrollWidth : 1;

      this.progress.style.setProperty('--go-progress-size', String(Math.min(visible, 1)));
      this.progress.style.setProperty(
        '--go-progress-offset',
        String(overflow > 0 ? this.viewport.scrollLeft / overflow : 0)
      );
    }

    if (!this.dots.length || !this.items.length) return;

    // L'element actif est celui dont le bord gauche est le plus proche du bord
    // gauche visible : c'est exactement ce sur quoi l'accroche vient se caler.
    const origin = this.viewport.scrollLeft;
    let active = 0;
    let closest = Infinity;

    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - this.viewport.offsetLeft - origin);
      if (distance < closest) {
        closest = distance;
        active = index;
      }
    });

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === active);
    });
  }
}

// Une page peut porter plusieurs sections `multicolumn` — l'accueil en a deja
// deux — et chacune emet sa balise <script src> des que le carrousel est actif.
// Deux balises identiques s'executent deux fois : sans cette garde, le second
// passage redefinit un nom deja pris et leve une exception.
if (!customElements.get('go-carousel')) {
  customElements.define('go-carousel', GoCarousel);
}
